import React, { useState, useEffect } from 'react';
import { Chip, Grid, Paper, MenuItem, Stack, styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, FormControl, InputLabel, Select, Button} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

//component import
import AlertInfo from '../../../dataObjects/AlertInfo';
import AlertInfoDialog from '../../../components/elements/AlertInfoDialog';
import { StaffEmployeeRole, StaffRole } from '../dataObjects/StaffMemberRole';

const StyledTableRow = styled(TableRow)(() => ({
    '&:nth-of-type(odd)': {
        backgroundColor: '#fafafa',
    },
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const StaffManagement = (): JSX.Element => {

    const [staffMembers, setStaffMembers] = useState<StaffEmployeeRole[]>();
    const [allRoleTypes, setAllRoleTypes] = useState<StaffRole[]>();
    const [selectedStaffId, setSelectedStaffId] = useState<number>(-1);
    const [selectedRoleTypeId, setSelectedRoleTypeId] = useState<number>(-1);

    //payload
    useEffect(() => {
        getAllStaffMemberRoles();
        getAllRoleTypes();
    }, []);


    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();
    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
    }

    const getAllRoleTypes = async () => {
        await fetch(`/IT/GetAllRoleTypes`)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: IT - GetAllRoleTypes',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    setAllRoleTypes(result.result);
                }
            }
        )
    }

    const getAllStaffMemberRoles = async () => {
        await fetch(`/IT/getAllStaffMemberRoles`)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: IT - getAllStaffMemberRoles',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    setStaffMembers(result.result);
                }
            }
            )
    }

    const updateSelectedStaffId = (event: any) => {
        setSelectedStaffId(event.target.value);
    };

    const updateSelectedRoleTypeId = (event: any) => {
        setSelectedRoleTypeId(event.target.value);
    };

    const saveStaffRole = () => {
        if (selectedStaffId > 0 && selectedRoleTypeId > 0) {
            fetch(`/It/SaveStaffMemberRole?employeeId=${selectedStaffId}&roleTypeId=${selectedRoleTypeId}`)
                .then(result => result.json())
                .then(result => {
                    if (result.isError) {
                        let info = {
                            type: 'error',
                            title: 'Error: IT - SaveStaffMemberRole',
                            errorMessage: result.errorMessage,
                            secondaryErrorMessage: result.secondaryErrorMessage
                        };
                        setAlertInfo(info);
                        setAlertInfoDialog(true);
                    }
                    else {
                        getAllStaffMemberRoles();
                        setSelectedRoleTypeId(-1);
                        setSelectedStaffId(-1);
                    }
                }
            )
        }
    }

    const removeStaffRole = (employeeId: number, roleTypeId: number) => {
        if (employeeId > 0 && roleTypeId > 0) {
            fetch(`/It/RemoveStaffMemberRole?employeeId=${employeeId}&roleTypeId=${roleTypeId}`)
                .then(result => result.json())
                .then(result => {
                    if (result.isError) {
                        let info = {
                            type: 'error',
                            title: 'Error: IT - RemoveStaffMemberRole',
                            errorMessage: result.errorMessage,
                            secondaryErrorMessage: result.secondaryErrorMessage
                        };
                        setAlertInfo(info);
                        setAlertInfoDialog(true);
                    }
                    else {
                        getAllStaffMemberRoles();
                    }
                }
            )
        }
    }

    const displayStaffRoles = (staff: StaffEmployeeRole) => {
        let roles = staff.roles
        if (roles.length > 0) {
            return (
                <Stack spacing={1} >
                    {
                        roles.map((role: StaffRole, index) =>
                            <Chip
                                sx={{
                                    "& .MuiChip-deleteIcon": {
                                        opacity: 0,
                                        transition: "opacity 0.2s ease-in-out",
                                    },
                                    "&:hover .MuiChip-deleteIcon": {
                                        opacity: 1,
                                    },
                                    width:'fit-content'
                                }}
                                key={index}
                                label={`${role.description}`}
                                color = "primary"
                                variant="outlined"
                                onDelete={() => removeStaffRole(role.employeeId, role.roleTypeId)} ></Chip>
                         )
                    }
                </Stack>
            )
        }
        else {
            return ("");
        }
    }

    return (
        <>
            <Grid container display="flex" justifyContent="flex-start">
                <Grid item xs={12} marginTop={2} >
                    <Typography component="h2" variant="h6" sx={{ mb: 2 }} fontWeight={550} >
                        Staff Roles
                    </Typography>
                </Grid>
                <Grid item xs={11} marginTop={1} marginLeft={10} >
                    <Stack direction="row" spacing={2} alignItems="center" display="flex" justifyContent="flex-start">
                        <Typography variant="body1">Add the Staff Role:</Typography>
                        <FormControl size="small">
                            <InputLabel>Staff</InputLabel>
                            <Select
                                value={selectedStaffId}
                                onChange={updateSelectedStaffId}
                                label="Staff"
                                sx={{ width: 300 }}
                            >

                                <MenuItem value={-1}>Please select</MenuItem>
                                {
                                    staffMembers && staffMembers.map((staff: StaffEmployeeRole) => (
                                        <MenuItem key={staff.employeeId} value={staff.employeeId}>{staff.name}</MenuItem>
                                    )
                                   )
                                }
                            </Select>
                        </FormControl>
                        <FormControl size="small">
                            <InputLabel>Roles</InputLabel>
                            <Select
                                value={selectedRoleTypeId}
                                onChange={updateSelectedRoleTypeId}
                                label="Roles"
                                sx={{ width: 300 }}
                            >

                                <MenuItem value={-1}>Please select</MenuItem>
                                {
                                    allRoleTypes && allRoleTypes.map((role: StaffRole) => (
                                        <MenuItem key={role.roleTypeId} value={role.roleTypeId}>{role.description}</MenuItem>
                                    )
                                   )
                                }
                            </Select>
                        </FormControl>
                        <FormControl size="small" variant="outlined">
                            <Button variant="outlined" startIcon={<AddIcon />} onClick={saveStaffRole} >
                                Add
                            </Button>
                        </FormControl>
                    </Stack>
                </Grid>
                <Grid item xs={10} marginTop={3} marginLeft={10} display="flex" justifyContent="flex-start">
                    {
                        staffMembers && staffMembers.length > 0 &&
                        <TableContainer component={Paper}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#FFFFFF' }}>
                                        <TableCell><Typography variant='body1' fontWeight={550}>Staff</Typography></TableCell>
                                        <TableCell><Typography variant='body1' fontWeight={550}>Roles</Typography></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                        {staffMembers.map((staff: StaffEmployeeRole) =>
                                        <StyledTableRow key={staff.employeeId}>
                                            <TableCell width="30%">
                                                <Typography variant="body1">{staff.name}</Typography>
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    whiteSpace: 'normal',
                                                    wordWrap: 'break-word',
                                                    maxWidth: "25%"
                                                }}
                                                >
                                                    {displayStaffRoles(staff)}
                                            </TableCell>
                                        </StyledTableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    }
                </Grid>
            </Grid>
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog}></AlertInfoDialog>
            }
        </>
    );
}

export default StaffManagement;